/**
 * SleepStageTrendChart component - Sleep stage composition visualization.
 *
 * Displays sleep stage breakdown with:
 * - Daily sleep stage composition (stacked area chart)
 * - 7-day vs 30-day comparison (bar chart)
 * - Percentage breakdown
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { SleepStageTrendData } from '../../types/progress';

interface SleepStageTrendChartProps {
  data: SleepStageTrendData;
}

const SLEEP_STAGE_COLORS = {
  deep: '#2563eb',
  light: '#60a5fa',
  rem: '#a855f7',
  awake: '#fb923c'
};

const SLEEP_STAGE_LABELS = {
  deep: '🌊 Deep Sleep',
  light: '☁️ Light Sleep',
  rem: '💭 REM Sleep',
  awake: '👁️ Awake Time'
};

const SleepStageTrendChart: React.FC<SleepStageTrendChartProps> = ({ data }) => {
  // Format comparison data
  const comparisonData = [
    {
      period: '7-Day Avg',
      deep: data.period_comparison.seven_day_avg.deep,
      light: data.period_comparison.seven_day_avg.light,
      rem: data.period_comparison.seven_day_avg.rem,
      awake: data.period_comparison.seven_day_avg.awake
    },
    {
      period: '30-Day Avg',
      deep: data.period_comparison.thirty_day_avg.deep,
      light: data.period_comparison.thirty_day_avg.light,
      rem: data.period_comparison.thirty_day_avg.rem,
      awake: data.period_comparison.thirty_day_avg.awake
    }
  ];

  return (
    <div className="card shadow-elegant animate-slide-up" style={{ animationDelay: '100ms' }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
            😴 Sleep Stage Breakdown
          </h3>
          <p className="text-neutral-600">
            Track the composition of your sleep over time
          </p>
        </div>
      </div>

      {/* Period Comparison */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* 7-Day Average */}
        <div className="bg-gradient-to-br from-primary-50 to-white border border-primary-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-neutral-700 mb-4">7-Day Average</h4>
          <div className="space-y-3">
            {Object.entries(SLEEP_STAGE_LABELS).map(([stage, label]) => (
              <div key={stage} className="flex items-center justify-between">
                <span className="text-sm text-neutral-700">{label}</span>
                <div className="flex items-baseline space-x-2">
                  <span className="text-lg font-bold" style={{ color: SLEEP_STAGE_COLORS[stage as keyof typeof SLEEP_STAGE_COLORS] }}>
                    {data.period_comparison.seven_day_avg[stage as keyof typeof data.period_comparison.seven_day_avg].toFixed(0)}
                  </span>
                  <span className="text-xs text-neutral-500">
                    ({data.period_comparison.percentages_7d[stage as keyof typeof data.period_comparison.percentages_7d].toFixed(0)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-primary-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-neutral-800">Total Sleep</span>
              <div className="text-lg font-bold text-primary-700">
                {data.period_comparison.seven_day_avg.total.toFixed(0)} min
              </div>
            </div>
          </div>
        </div>

        {/* 30-Day Average */}
        <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-neutral-700 mb-4">30-Day Average</h4>
          <div className="space-y-3">
            {Object.entries(SLEEP_STAGE_LABELS).map(([stage, label]) => (
              <div key={stage} className="flex items-center justify-between">
                <span className="text-sm text-neutral-700">{label}</span>
                <div className="flex items-baseline space-x-2">
                  <span className="text-lg font-bold" style={{ color: SLEEP_STAGE_COLORS[stage as keyof typeof SLEEP_STAGE_COLORS] }}>
                    {data.period_comparison.thirty_day_avg[stage as keyof typeof data.period_comparison.thirty_day_avg].toFixed(0)}
                  </span>
                  <span className="text-xs text-neutral-500">
                    ({data.period_comparison.percentages_30d[stage as keyof typeof data.period_comparison.percentages_30d].toFixed(0)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-neutral-800">Total Sleep</span>
              <div className="text-lg font-bold text-blue-700">
                {data.period_comparison.thirty_day_avg.total.toFixed(0)} min
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Chart */}
      <div className="bg-gradient-to-br from-neutral-50 to-white p-6 rounded-xl border border-neutral-200">
        <h4 className="text-base font-semibold text-neutral-800 mb-4">Period Comparison</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={comparisonData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="period"
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#6b7280' }}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#6b7280' }}
              label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '2px solid #C98CCC',
                borderRadius: '0.75rem',
                padding: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
            />
            <Bar dataKey="deep" fill={SLEEP_STAGE_COLORS.deep} name="Deep Sleep" />
            <Bar dataKey="light" fill={SLEEP_STAGE_COLORS.light} name="Light Sleep" />
            <Bar dataKey="rem" fill={SLEEP_STAGE_COLORS.rem} name="REM Sleep" />
            <Bar dataKey="awake" fill={SLEEP_STAGE_COLORS.awake} name="Awake Time" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Data Completeness */}
      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-neutral-600">
          Data completeness: <span className="font-semibold text-neutral-900">{data.data_completeness.toFixed(1)}%</span>
        </div>
        <div className="text-neutral-500">
          {data.daily_breakdown.length} days with sleep stage data
        </div>
      </div>
    </div>
  );
};

export default SleepStageTrendChart;
